import React from 'react';
import solar from "./img/solar-hero.png";
import slogo from "./img/slogo.png";
import quality from "./img/quality.jpg";
import { Shield, Zap, Award, ArrowRight, Check, Sun, Wifi, Cog } from 'lucide-react';

function Section() {
  return (
    <section className="py-20 md:py-28 px-6 md:px-10" style={{ background: '#ffffff' }}>
      <div className="max-w-7xl mx-auto">

        {/* Header */}
        <div className="text-center mb-14 md:mb-18">
          <span className="text-[11px] font-semibold tracking-[0.2em] uppercase text-[#712B35] bg-[#712B35]/5 px-3 py-1 rounded-full inline-block mb-3">
            About Us
          </span>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mt-2 mb-2 text-[#2a2118]">
            We Power Your <span className="text-[#712B35]">Future</span>
          </h2>
          <p className="text-sm md:text-base max-w-2xl mx-auto font-light text-[#5a524c]">
            Transforming spaces with cutting-edge technology and sustainable solutions.
          </p>
        </div>

        {/* Top Row: Image + Company Info side by side */}
        <div className="grid lg:grid-cols-2 gap-8 mb-8">

          {/* Image card */}
          <div className="relative group overflow-hidden rounded-3xl h-[340px] md:h-[420px] shadow-xl shadow-black/5">
            <img
              src={solar}
              alt="Solar installation"
              className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
            <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-1.5 h-1.5 rounded-full bg-[#b89b6e]" />
                <span className="text-white/60 text-xs font-medium tracking-widest uppercase">Featured Work</span>
              </div>
              <h3 className="text-white text-lg md:text-xl font-bold">Professional Solar Installation</h3>
              <p className="text-white/50 text-sm mt-1">Eco-friendly energy for modern living</p>
            </div>
          </div>

          {/* Company info */}
          <div className="bg-white rounded-3xl p-7 md:p-9 border border-gray-100 shadow-xl shadow-black/5 flex flex-col justify-center">
            <div className="flex items-center gap-4 mb-5 pb-5 border-b border-gray-100">
              <div className="w-14 h-14 rounded-2xl flex items-center justify-center flex-shrink-0 shadow-lg shadow-[#712B35]/20" style={{ background: '#712B35' }}>
                <img src={slogo} alt="" className="w-8 h-6 object-contain" />
              </div>
              <div>
                <p className="font-bold text-base text-[#2a2118]">Torchbearer</p>
                <p className="text-xs font-medium text-[#712B35]">Technologies</p>
              </div>
            </div>

            <p className="text-sm leading-relaxed mb-6 text-[#5a524c]">
              We provide modern CCTV systems, solar installation and electrical wiring that enhance safety and maximize energy efficiency.
            </p>

            <div className="grid grid-cols-1 gap-2.5">
              {[
                { icon: <Shield className="w-4 h-4" />, text: "Certified & Licensed" },
                { icon: <Zap className="w-4 h-4" />, text: "Energy Efficient" },
                { icon: <Award className="w-4 h-4" />, text: "Trusted Quality" }
              ].map((f, i) => (
                <div key={i} className="flex items-center gap-3 py-3 px-4 rounded-xl text-sm font-medium transition-all duration-300 hover:translate-x-1" style={{ background: '#f8f7f5', color: '#2a2118' }}>
                  <span className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: '#712B35', color: 'white' }}>{f.icon}</span>
                  {f.text}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Bottom Row: Three cards */}
        <div className="grid md:grid-cols-3 gap-6">

          {/* Quality Card */}
          <div className="rounded-3xl p-7 md:p-9 text-white flex flex-col relative overflow-hidden group transition-all duration-500 hover:-translate-y-1 hover:shadow-2xl"
            style={{ background: 'linear-gradient(160deg, #712B35 0%, #4a1d2a 100%)' }}>
            <div className="absolute top-0 right-0 w-64 h-64 rounded-full opacity-[0.08] bg-white -translate-y-1/3 translate-x-1/3 group-hover:scale-110 transition-transform duration-700" />
            
            <div className="flex items-center gap-4 mb-6">
              <div className="relative flex-shrink-0">
                <img src={quality} alt="" className="w-16 h-16 object-cover rounded-2xl border-2 border-white/20 shadow-lg" />
                <div className="absolute -bottom-1.5 -right-1.5 bg-white rounded-full p-1.5 shadow-lg">
                  <Award className="w-3.5 h-3.5 text-[#712B35]" />
                </div>
              </div>
              <div>
                <h3 className="text-lg font-bold">Quality First</h3>
                <p className="text-white/60 text-xs font-light leading-relaxed mt-0.5">
                  We deliver reliable, durable, and expertly installed systems.
                </p>
              </div>
            </div>
            
            <div className="space-y-2.5 mb-auto">
              {[
                { icon: <Check className="w-3.5 h-3.5" />, text: "Industry-Leading Standards" },
                { icon: <Check className="w-3.5 h-3.5" />, text: "Premium Components" },
                { icon: <Check className="w-3.5 h-3.5" />, text: "Warranty Protected" }
              ].map((item, i) => (
                <div key={i} className="flex items-center gap-3 py-2.5 px-3.5 rounded-xl text-xs font-medium backdrop-blur-sm" style={{ background: 'rgba(255,255,255,0.08)' }}>
                  <span className="w-6 h-6 rounded-lg flex items-center justify-center flex-shrink-0 bg-white/10">{item.icon}</span>
                  {item.text}
                </div>
              ))}
            </div>
            
            <p className="text-white/50 text-xs font-light mt-5 pt-4 border-t border-white/10">
              Our mission is to offer solutions you can trust — safe, sustainable, and efficient for your home or business.
            </p>
          </div>

          {/* Features Card */}
          <div className="bg-white rounded-3xl p-7 md:p-9 border border-gray-100 flex flex-col transition-all duration-500 hover:-translate-y-1 hover:shadow-2xl hover:shadow-black/5">
            <div className="w-14 h-14 rounded-2xl flex items-center justify-center mb-5 shadow-lg shadow-[#712B35]/20" style={{ background: '#712B35' }}>
              <Sun className="w-6 h-6 text-white" />
            </div>
            <h3 className="text-lg font-bold mb-2 text-[#2a2118]">Why Choose Us</h3>
            <p className="text-sm leading-relaxed mb-5 font-light text-[#5a524c]">
              From security systems to solar and electrical solutions, we offer expert installation built to last.
            </p>
            <div className="space-y-2.5 mb-auto">
              {[
                { icon: <Zap className="w-4 h-4" />, text: "Fast Installation" },
                { icon: <Shield className="w-4 h-4" />, text: "24/7 Support" },
                { icon: <Cog className="w-4 h-4" />, text: "Custom-Tailored Solutions" }
              ].map((f, i) => (
                <div key={i} className="flex items-center gap-3 py-3 px-4 rounded-xl text-sm font-medium transition-all duration-300 hover:translate-x-1" style={{ background: '#f8f7f5', color: '#2a2118' }}>
                  <span className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: '#712B35', color: 'white' }}>{f.icon}</span>
                  {f.text}
                </div>
              ))}
            </div>
          </div>

          {/* CTA Card */}
          <div className="rounded-3xl p-7 md:p-9 flex flex-col text-white relative overflow-hidden group transition-all duration-500 hover:-translate-y-1 hover:shadow-2xl"
            style={{ background: '#2a2118' }}>
            <div className="absolute top-0 right-0 w-64 h-64 rounded-full opacity-[0.06] bg-[#712B35] -translate-y-1/3 translate-x-1/3 group-hover:scale-110 transition-transform duration-700" />
            <div className="w-14 h-14 rounded-2xl flex items-center justify-center mb-5 shadow-lg" style={{ background: '#712B35' }}>
              <Wifi className="w-6 h-6 text-white" />
            </div>
            <h3 className="text-lg font-bold mb-2">Let's Build Something Exceptional</h3>
            <p className="text-white/50 text-xs font-light leading-relaxed mb-6">
              From security systems to solar and electrical solutions, we offer expert installation built to last.
            </p>
            <div className="space-y-2.5 mb-8">
              {[
                { icon: <Zap className="w-3.5 h-3.5" />, text: "Fast Installation" },
                { icon: <Shield className="w-3.5 h-3.5" />, text: "24/7 Support" },
                { icon: <Award className="w-3.5 h-3.5" />, text: "Custom-Tailored Solutions" }
              ].map((f, i) => (
                <div key={i} className="flex items-center gap-3 py-2.5 px-3.5 rounded-xl text-xs font-medium backdrop-blur-sm" style={{ background: 'rgba(255,255,255,0.06)' }}>
                  <span className="w-6 h-6 rounded-lg flex items-center justify-center flex-shrink-0 bg-white/10">{f.icon}</span>
                  {f.text}
                </div>
              ))}
            </div>
            <button
              className="w-full py-3.5 rounded-2xl font-semibold text-sm transition-all duration-300 hover:shadow-xl hover:-translate-y-0.5 flex items-center justify-center gap-2 text-white"
              style={{ background: '#712B35' }}
              onMouseEnter={(e) => e.currentTarget.style.background = '#5f2b40'}
              onMouseLeave={(e) => e.currentTarget.style.background = '#712B35'}
            >
              Learn More About Our Services
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>

        </div>
      </div>
    </section>
  );
}

export default Section;
